import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/types/supabase";

export interface UserGroup {
  id: number;
  name: string;
  description: string | null;
  criteria: Json;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: number;
  member_count: number;
  last_refreshed_at: string | null;
}

export interface GroupMember {
  user_id: number;
  name: string;
  email: string;
  avatar_url: string | null;
  created_at: string;
  subscription_status: string;
  subscription_plan_name: string;
  total_reading_seconds?: number;
  books_read_count?: number;
  highlights_count?: number;
  feedback_count?: number;
  audio_plays_count?: number;
  completion_rate?: number;
  account_age_days?: number;
  last_activity_days?: number;
}

export interface GroupCriteria {
  [key: string]: string | number | boolean;
}

// Helper function to get current user's profile ID
const getCurrentUserProfileId = async (): Promise<number> => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('User not authenticated');

  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('id')
    .eq('uid', user.user.id)
    .single();

  if (profileError || !profile) {
    throw new Error('User profile not found');
  }

  return profile.id;
};

// Helper function to chunk array into smaller arrays
const chunkArray = <T>(array: T[], chunkSize: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

// Helper function to get the latest subscription for each user
// Returns a Map of user_id -> latest subscription data
const getLatestSubscriptions = async (): Promise<Map<number, {
  status: string;
  subscription_end: string | null;
  subscription_plan_id: number | null;
  created_at: string;
}>> => {
  const { data: allSubscriptions } = await supabase
    .from('user_subscriptions')
    .select('user_id, status, subscription_end, subscription_plan_id, created_at')
    .order('created_at', { ascending: false });
  
  const latestSubscriptions = new Map<number, {
    status: string;
    subscription_end: string | null;
    subscription_plan_id: number | null;
    created_at: string;
  }>();
  
  allSubscriptions?.forEach(sub => {
    const existing = latestSubscriptions.get(sub.user_id);
    if (!existing || new Date(sub.created_at) > new Date(existing.created_at)) {
      latestSubscriptions.set(sub.user_id, {
        status: sub.status,
        subscription_end: sub.subscription_end,
        subscription_plan_id: sub.subscription_plan_id,
        created_at: sub.created_at
      });
    }
  });
  
  return latestSubscriptions;
};

// Helper function to fetch data with chunked user IDs to avoid URL length limits
const fetchWithChunkedUserIds = async <T>(
  table: string,
  select: string,
  userIds: number[],
  additionalFilters?: (query: any) => any,
  chunkSize: number = 100
): Promise<T[]> => {
  if (userIds.length === 0) return [];
  
  // If the array is small enough, fetch directly
  if (userIds.length <= chunkSize) {
    let query = (supabase
      .from(table as any)
      .select(select)
      .in('user_id', userIds) as any);
    
    if (additionalFilters) {
      query = additionalFilters(query);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as T[];
  }
  
  // Otherwise, chunk and fetch in batches
  const chunks = chunkArray(userIds, chunkSize);
  const allResults: T[] = [];
  
  for (const chunk of chunks) {
    let query = (supabase
      .from(table as any)
      .select(select)
      .in('user_id', chunk) as any);
    
    if (additionalFilters) {
      query = additionalFilters(query);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    if (data) {
      allResults.push(...(data as T[]));
    }
  }
  
  return allResults;
};

// Core function to get users based on criteria
export const getUsersByCriteria = async (criteria: GroupCriteria): Promise<GroupMember[]> => {
  try {
    let query = supabase
      .from('profiles')
      .select(`
        id,
        name,
        email,
        avatar_url,
        registered_at,
        is_active
      `)
      .eq('is_active', true);

    // Apply criteria filters
    if (criteria.subscription_status) {
      if (criteria.subscription_status === 'free') {
        // Free users: users without active subscriptions (includes no plan, expired, cancelled, etc.)
        const { data: activeSubscriptions } = await supabase
          .from('user_subscriptions')
          .select('user_id')
          .eq('status', 'active');
        
        const activeUserIds = activeSubscriptions?.map(row => row.user_id) || [];
        if (activeUserIds.length > 0) {
          // Use a different approach - filter out users with active subscriptions
          // We'll handle this after the main query by filtering the results
          // For now, let's get all users and filter them later
        }
      } else if (criteria.subscription_status === 'expired') {
        // Expired subscribers: users whose LATEST subscription has subscription_end < NOW()
        const latestSubscriptions = await getLatestSubscriptions();
        const now = new Date();
        
        // Filter to users whose latest subscription is expired
        const expiredUserIds = Array.from(latestSubscriptions.entries())
          .filter(([_, sub]) => sub.subscription_end && new Date(sub.subscription_end) < now)
          .map(([userId, _]) => userId);
        
        if (expiredUserIds.length > 0) {
          query = query.in('id', expiredUserIds);
        } else {
          return [];
        }
      } else if (criteria.subscription_status === 'cancelled') {
        // Cancelled subscribers: users with cancelled subscriptions
        const { data: cancelledSubscriptions } = await supabase
          .from('user_subscriptions')
          .select('user_id')
          .eq('status', 'cancelled');
        
        const userIds = cancelledSubscriptions?.map(row => row.user_id) || [];
        if (userIds.length > 0) {
          query = query.in('id', userIds);
        } else {
          return [];
        }
      } else if (criteria.subscription_status === 'no_plan') {
        // Users who have never had any subscription (no records in user_subscriptions)
        const { data: allSubscriptions } = await supabase
          .from('user_subscriptions')
          .select('user_id');
        
        const subscribedUserIds = allSubscriptions?.map(row => row.user_id) || [];
        if (subscribedUserIds.length > 0) {
          // We'll handle this after the main query by filtering the results
        }
      } else if (criteria.subscription_status === 'active') {
        // Active subscribers: users whose LATEST subscription is active with subscription_end > NOW()
        const latestSubscriptions = await getLatestSubscriptions();
        const now = new Date();
        
        // Filter to users whose latest subscription is active and not expired
        const activeUserIds = Array.from(latestSubscriptions.entries())
          .filter(([_, sub]) => {
            // Must be active status
            if (sub.status !== 'active') return false;
            // Include only if subscription_end exists AND is in the future
            return sub.subscription_end && new Date(sub.subscription_end) > now;
          })
          .map(([userId, _]) => userId);
        
        if (activeUserIds.length > 0) {
          query = query.in('id', activeUserIds);
        } else {
          return [];
        }
      } else {
        // Users with specific subscription status (other than active/expired/cancelled/free/no_plan)
        const { data: subscriptions } = await supabase
          .from('user_subscriptions')
          .select('user_id')
          .eq('status', String(criteria.subscription_status));
        
        const userIds = subscriptions?.map(row => row.user_id) || [];
        if (userIds.length > 0) {
          query = query.in('id', userIds);
        } else {
          return [];
        }
      }
    }

    if (criteria.subscription_tier) {
      const subscriptionQuery = supabase
        .from('user_subscriptions')
        .select('user_id')
        .eq('tier', String(criteria.subscription_tier)) as any;
      
      const { data: subscriptions } = await subscriptionQuery;
      const userIds = subscriptions?.map(row => row.user_id) || [];
      if (userIds.length > 0) {
        query = query.in('id', userIds);
      } else {
        return [];
      }
    }

    if (criteria.registration_date_after) {
      query = query.gte('registered_at', criteria.registration_date_after);
    }

    if (criteria.last_activity_days) {
      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - (criteria.last_activity_days as number));
      query = query.lte('last_sign_in_at', daysAgo.toISOString());
    }

    if (criteria.books_read_count) {
      const { data: reads } = await supabase
        .from('book_reads')
        .select('user_id');
      
      const userReadCounts = (reads || []).reduce((acc, read) => {
        acc[read.user_id] = (acc[read.user_id] || 0) + 1;
        return acc;
      }, {} as Record<number, number>);
      
      const userIds = Object.entries(userReadCounts)
        .filter(([_, count]) => count >= (criteria.books_read_count as number))
        .map(([userId, _]) => parseInt(userId));
      
      if (userIds.length > 0) {
        query = query.in('id', userIds);
      } else {
        return [];
      }
    }

    if (criteria.total_reading_minutes) {
      const minSeconds = (criteria.total_reading_minutes as number) * 60;
      const { data: reads } = await supabase
        .from('book_reads')
        .select('user_id, active_seconds');
      
      const userReadingTime = (reads || []).reduce((acc, read) => {
        acc[read.user_id] = (acc[read.user_id] || 0) + (read.active_seconds || 0);
        return acc;
      }, {} as Record<number, number>);
      
      const userIds = Object.entries(userReadingTime)
        .filter(([_, seconds]) => seconds >= minSeconds)
        .map(([userId, _]) => parseInt(userId));
      
      if (userIds.length > 0) {
        query = query.in('id', userIds);
      } else {
        return [];
      }
    }

    if (criteria.highlights_count) {
      const { data: highlights } = await supabase
        .from('user_highlights')
        .select('user_id');
      
      const userHighlightCounts = (highlights || []).reduce((acc, highlight) => {
        acc[highlight.user_id] = (acc[highlight.user_id] || 0) + 1;
        return acc;
      }, {} as Record<number, number>);
      
      const userIds = Object.entries(userHighlightCounts)
        .filter(([_, count]) => count >= (criteria.highlights_count as number))
        .map(([userId, _]) => parseInt(userId));
      
      if (userIds.length > 0) {
        query = query.in('id', userIds);
      } else {
        return [];
      }
    }

    if (criteria.feedback_count) {
      const { data: feedbacks } = await supabase
        .from('user_book_feedback')
        .select('user_id');
      
      const userFeedbackCounts = (feedbacks || []).reduce((acc, feedback) => {
        acc[feedback.user_id] = (acc[feedback.user_id] || 0) + 1;
        return acc;
      }, {} as Record<number, number>);
      
      const userIds = Object.entries(userFeedbackCounts)
        .filter(([_, count]) => count >= (criteria.feedback_count as number))
        .map(([userId, _]) => parseInt(userId));
      
      if (userIds.length > 0) {
        query = query.in('id', userIds);
      } else {
        return [];
      }
    }

    if (criteria.selected_categories_count) {
      const { data: categories } = await supabase
        .from('user_categories')
        .select('profile_id');
      
      const userCategoryCounts = (categories || []).reduce((acc, category) => {
        acc[category.profile_id] = (acc[category.profile_id] || 0) + 1;
        return acc;
      }, {} as Record<number, number>);
      
      const userIds = Object.entries(userCategoryCounts)
        .filter(([_, count]) => count >= (criteria.selected_categories_count as number))
        .map(([userId, _]) => parseInt(userId));
      
      if (userIds.length > 0) {
        query = query.in('id', userIds);
      } else {
        return [];
      }
    }

    if (criteria.book_views_count) {
      const { data: views } = await supabase
        .from('book_views')
        .select('user_id');
      
      const userViewCounts = (views || []).reduce((acc, view) => {
        acc[view.user_id] = (acc[view.user_id] || 0) + 1;
        return acc;
      }, {} as Record<number, number>);
      
      const userIds = Object.entries(userViewCounts)
        .filter(([_, count]) => count >= (criteria.book_views_count as number))
        .map(([userId, _]) => parseInt(userId));
      
      if (userIds.length > 0) {
        query = query.in('id', userIds);
      } else {
        return [];
      }
    }

    if (criteria.audio_plays_count) {
      const { data: plays } = await supabase
        .from('book_audio_plays')
        .select('user_id');
      
      const userAudioCounts = (plays || []).reduce((acc, play) => {
        acc[play.user_id] = (acc[play.user_id] || 0) + 1;
        return acc;
      }, {} as Record<number, number>);
      
      const userIds = Object.entries(userAudioCounts)
        .filter(([_, count]) => count >= (criteria.audio_plays_count as number))
        .map(([userId, _]) => parseInt(userId));
      
      if (userIds.length > 0) {
        query = query.in('id', userIds);
      } else {
        return [];
      }
    }

    if (criteria.completion_rate) {
      const minRate = criteria.completion_rate as number;
      const { data: reads } = await supabase
        .from('book_reads')
        .select('user_id, completed');
      
      const userCompletionRates = (reads || []).reduce((acc, read) => {
        if (!acc[read.user_id]) {
          acc[read.user_id] = { total: 0, completed: 0 };
        }
        acc[read.user_id].total += 1;
        if (read.completed) {
          acc[read.user_id].completed += 1;
        }
        return acc;
      }, {} as Record<number, { total: number; completed: number }>);
      
      const userIds = Object.entries(userCompletionRates)
        .filter(([_, stats]) => {
          const rate = (stats.completed / stats.total) * 100;
          return rate >= minRate;
        })
        .map(([userId, _]) => parseInt(userId));
      
      if (userIds.length > 0) {
        query = query.in('id', userIds);
      } else {
        return [];
      }
    }

    if (criteria.subscription_duration_days) {
      const minDays = criteria.subscription_duration_days as number;
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - minDays);
      
      const { data: subscriptions } = await supabase
        .from('user_subscriptions')
        .select('user_id, created_at')
        .eq('status', 'active')
        .lte('created_at', cutoffDate.toISOString());
      
      const userIds = subscriptions?.map(row => row.user_id) || [];
      if (userIds.length > 0) {
        query = query.in('id', userIds);
      } else {
        return [];
      }
    }

    if (criteria.registration_days) {
      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - (criteria.registration_days as number));
      query = query.gte('registered_at', daysAgo.toISOString());
    }

    if (criteria.account_age_days) {
      // Users registered more than X days ago
      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - (criteria.account_age_days as number));
      query = query.lte('registered_at', daysAgo.toISOString());
    }

    if (criteria.account_age_min_days) {
      // Users registered at least X days ago
      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - (criteria.account_age_min_days as number));
      query = query.lte('registered_at', daysAgo.toISOString());
    }

    if (criteria.account_age_max_days) {
      // Users registered within X days
      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - (criteria.account_age_max_days as number));
      query = query.gte('registered_at', daysAgo.toISOString());
    }

    if (criteria.overall_engagement_score) {
      // This would require a more complex calculation based on multiple factors
      // For now, we'll use a simplified approach
      const { data: reads } = await supabase
        .from('book_reads')
        .select('user_id, active_seconds');
      
      const userEngagement = (reads || []).reduce((acc, read) => {
        if (!acc[read.user_id]) {
          acc[read.user_id] = { totalSeconds: 0, readCount: 0 };
        }
        acc[read.user_id].totalSeconds += read.active_seconds || 0;
        acc[read.user_id].readCount += 1;
        return acc;
      }, {} as Record<number, { totalSeconds: number; readCount: number }>);
      
      // Simple engagement score: (total_seconds / 60) + (read_count * 2)
      const userIds = Object.entries(userEngagement)
        .filter(([_, stats]) => {
          const score = (stats.totalSeconds / 60) + (stats.readCount * 2);
          return score >= (criteria.overall_engagement_score as number);
        })
        .map(([userId, _]) => parseInt(userId));
      
      if (userIds.length > 0) {
        query = query.in('id', userIds);
      } else {
        return [];
      }
    }

    if (criteria.age_group) {
      query = query.eq('age_group', String(criteria.age_group));
    }

    if (criteria.referred_by) {
      if (criteria.referred_by === 'not_null') {
        query = query.not('referred_by', 'is', null);
      } else {
        query = query.eq('referred_by', Number(criteria.referred_by));
      }
    }

    if (criteria.platform_preference) {
      // This would require tracking platform usage in a separate table
      // For now, we'll skip this criteria
    }

    if (criteria.content_preference) {
      // This would require analyzing user behavior across different content types
      // For now, we'll skip this criteria
    }

    if (criteria.reading_frequency) {
      // This would require analyzing reading patterns over time
      // For now, we'll skip this criteria
    }

    if (criteria.highlights_used) {
      const { data: highlights } = await supabase
        .from('user_highlights')
        .select('user_id');
      
      const userIds = highlights?.map(row => row.user_id) || [];
      if (userIds.length > 0) {
        query = query.in('id', userIds);
      } else {
        return [];
      }
    }

    if (criteria.feedback_provided) {
      const { data: feedbacks } = await supabase
        .from('user_book_feedback')
        .select('user_id');
      
      const userIds = feedbacks?.map(row => row.user_id) || [];
      if (userIds.length > 0) {
        query = query.in('id', userIds);
      } else {
        return [];
      }
    }

    // Fetch all profiles with pagination (Supabase has 1000 row limit)
    const allProfiles: Array<{
      id: number;
      name: string;
      email: string;
      avatar_url: string | null;
      registered_at: string;
      is_active: boolean;
    }> = [];
    let from = 0;
    const pageSize = 1000;
    let hasMore = true;

    while (hasMore) {
      const { data: profiles, error } = await query.range(from, from + pageSize - 1);

      if (error) {
        console.error("Error fetching users by criteria:", error);
        throw error;
      }

      if (profiles && profiles.length > 0) {
        allProfiles.push(...profiles);
        hasMore = profiles.length === pageSize;
        from += pageSize;
      } else {
        hasMore = false;
      }
    }

    if (allProfiles.length === 0) {
      return [];
    }

    const profiles = allProfiles;

    // Post-query filtering for complex subscription status criteria
    let filteredProfiles = profiles;
    
    if (criteria.subscription_status === 'free') {
      // Filter out users with active subscriptions
      const { data: activeSubscriptions } = await supabase
        .from('user_subscriptions')
        .select('user_id')
        .eq('status', 'active');
      
      const activeUserIds = activeSubscriptions?.map(row => row.user_id) || [];
      filteredProfiles = profiles.filter(profile => !activeUserIds.includes(profile.id));
    } else if (criteria.subscription_status === 'no_plan') {
      // Filter out users who have ever had any subscription
      const { data: allSubscriptions } = await supabase
        .from('user_subscriptions')
        .select('user_id');
      
      const subscribedUserIds = allSubscriptions?.map(row => row.user_id) || [];
      filteredProfiles = profiles.filter(profile => !subscribedUserIds.includes(profile.id));
    }

    // Get subscription information for each user (chunked to avoid URL length limits)
    // Fetch ALL subscriptions (not just active) so we can properly determine status and fetch reading time for expired users
    const userIds = filteredProfiles.map(p => p.id);
    const subscriptions = await fetchWithChunkedUserIds<{
      user_id: number;
      status: string;
      subscription_plan_id: number;
      subscription_end: string | null;
      created_at: string;
    }>(
      'user_subscriptions',
      'user_id, status, subscription_plan_id, subscription_end, created_at',
      userIds,
      undefined, // Don't filter by status - fetch all subscriptions to include expired ones
      100 // Chunk size of 100 to keep URLs manageable
    );

    // Get plan names separately
    const planIds = subscriptions?.map(sub => sub.subscription_plan_id) || [];
    const { data: plans } = await supabase
      .from('subscription_plans')
      .select('id, name')
      .in('id', planIds);

    const planMap = new Map();
    plans?.forEach(plan => {
      planMap.set(plan.id, plan.name);
    });

    const subscriptionMap = new Map();
    const now = new Date();
    
    // Group subscriptions by user_id and get the most recent one for each user
    const latestSubscriptionsByUser = new Map<number, {
      user_id: number;
      status: string;
      subscription_plan_id: number;
      subscription_end: string | null;
      created_at: string;
    }>();
    
    subscriptions?.forEach(sub => {
      const existing = latestSubscriptionsByUser.get(sub.user_id);
      if (!existing || new Date(sub.created_at || sub.subscription_end || '') > new Date(existing.created_at || existing.subscription_end || '')) {
        latestSubscriptionsByUser.set(sub.user_id, {
          user_id: sub.user_id,
          status: sub.status,
          subscription_plan_id: sub.subscription_plan_id,
          subscription_end: sub.subscription_end,
          created_at: sub.created_at || sub.subscription_end || ''
        });
      }
    });
    
    // Map the latest subscription for each user
    latestSubscriptionsByUser.forEach(sub => {
      // Determine actual status based on subscription_end
      let actualStatus = sub.status;
      if (sub.subscription_end) {
        if (new Date(sub.subscription_end) < now) {
          // Past end date = expired
          actualStatus = 'expired';
        } else if (sub.status === 'active' && new Date(sub.subscription_end) > now) {
          // Active with valid end date = active
          actualStatus = 'active';
        }
      } else if (sub.status === 'active') {
        // Active with NULL end date = not considered active (must have end date > NOW)
        actualStatus = 'free';
      }
      
      subscriptionMap.set(sub.user_id, {
        status: actualStatus,
        plan_name: planMap.get(sub.subscription_plan_id) || 'No Plan'
      });
    });

    // Get additional metrics based on criteria
    const metricsData: any = {};
    
    // Always fetch reading time for all users (not just when criteria require it)
    const readingData = await fetchWithChunkedUserIds<{
      user_id: number;
      active_seconds: number | null;
    }>(
      'book_reads',
      'user_id, active_seconds',
      userIds,
      undefined,
      100
    );

    const readingTimeMap = new Map();
    readingData.forEach(read => {
      if (read.user_id && read.active_seconds) {
        const currentTotal = readingTimeMap.get(read.user_id) || 0;
        readingTimeMap.set(read.user_id, currentTotal + read.active_seconds);
      }
    });
    metricsData.readingTime = readingTimeMap;

    // Fetch books read count if relevant
    if (criteria.books_read_count) {
      const booksData = await fetchWithChunkedUserIds<{
        user_id: number;
      }>(
        'book_reads',
        'user_id',
        userIds,
        (query) => query.eq('is_completed', true),
        100
      );

      const booksCountMap = new Map();
      booksData.forEach(read => {
        const currentCount = booksCountMap.get(read.user_id) || 0;
        booksCountMap.set(read.user_id, currentCount + 1);
      });
      metricsData.booksRead = booksCountMap;
    }

    // Fetch highlights count if relevant
    if (criteria.highlights_count) {
      const highlightsData = await fetchWithChunkedUserIds<{
        user_id: number;
      }>(
        'user_highlights',
        'user_id',
        userIds,
        undefined,
        100
      );

      const highlightsCountMap = new Map();
      highlightsData.forEach(highlight => {
        const currentCount = highlightsCountMap.get(highlight.user_id) || 0;
        highlightsCountMap.set(highlight.user_id, currentCount + 1);
      });
      metricsData.highlights = highlightsCountMap;
    }

    // Fetch feedback count if relevant
    if (criteria.feedback_count) {
      const feedbackData = await fetchWithChunkedUserIds<{
        user_id: number;
      }>(
        'user_book_feedback',
        'user_id',
        userIds,
        undefined,
        100
      );

      const feedbackCountMap = new Map();
      feedbackData.forEach(feedback => {
        const currentCount = feedbackCountMap.get(feedback.user_id) || 0;
        feedbackCountMap.set(feedback.user_id, currentCount + 1);
      });
      metricsData.feedback = feedbackCountMap;
    }

    // Fetch audio plays count if relevant
    if (criteria.audio_plays_count) {
      const audioData = await fetchWithChunkedUserIds<{
        user_id: number;
      }>(
        'book_reads',
        'user_id',
        userIds,
        (query) => query.not('audio_duration', 'is', null),
        100
      );

      const audioCountMap = new Map();
      audioData.forEach(read => {
        const currentCount = audioCountMap.get(read.user_id) || 0;
        audioCountMap.set(read.user_id, currentCount + 1);
      });
      metricsData.audioPlays = audioCountMap;
    }

    // Calculate account age if relevant
    if (criteria.account_age_min_days || criteria.account_age_max_days || criteria.account_age_days) {
      const accountAgeMap = new Map();
      const now = new Date();
      filteredProfiles.forEach(profile => {
        const createdDate = new Date(profile.registered_at);
        const daysDiff = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
        accountAgeMap.set(profile.id, daysDiff);
      });
      metricsData.accountAge = accountAgeMap;
    }

    // Format the results
    return filteredProfiles.map(profile => ({
      user_id: profile.id,
      name: profile.name,
      email: profile.email,
      avatar_url: profile.avatar_url,
      created_at: profile.registered_at,
      subscription_status: subscriptionMap.get(profile.id)?.status || 'free',
      subscription_plan_name: subscriptionMap.get(profile.id)?.plan_name || 'No Plan',
      total_reading_seconds: metricsData.readingTime?.get(profile.id) || 0,
      books_read_count: metricsData.booksRead?.get(profile.id) || 0,
      highlights_count: metricsData.highlights?.get(profile.id) || 0,
      feedback_count: metricsData.feedback?.get(profile.id) || 0,
      audio_plays_count: metricsData.audioPlays?.get(profile.id) || 0,
      account_age_days: metricsData.accountAge?.get(profile.id) || 0
    }));

  } catch (error) {
    console.error("Error in getUsersByCriteria:", error);
    throw error;
  }
};

// Function to count users based on criteria (optimized with database-level COUNT)
export const countUsersByCriteria = async (criteria: GroupCriteria): Promise<number> => {
  try {
    // Handle combination of subscription_status and total_reading_minutes
    if (criteria.subscription_status === 'free' && criteria.total_reading_minutes) {
      const minSeconds = (criteria.total_reading_minutes as number) * 60;
      
      // Get truly active subscription user IDs (status='active' AND subscription_end >= NOW() or NULL)
      // Users with expired, cancelled, or past-end subscriptions should be considered "free"
      const { data: activeSubUsers } = await supabase
        .from('user_subscriptions')
        .select('user_id, subscription_end')
        .eq('status', 'active');
      
      // Filter to only truly active subscriptions (subscription_end > NOW())
      // Note: subscription_end must be > NOW(), NULL values are excluded
      const now = new Date();
      const trulyActiveUserIds = new Set(
        activeSubUsers
          ?.filter(sub => {
            // Include only if subscription_end exists AND is in the future
            return sub.subscription_end && new Date(sub.subscription_end) > now;
          })
          .map(s => s.user_id) || []
      );
      
      // Get all free users (without truly active subscriptions)
      const { data: allFreeProfiles } = await supabase
        .from('profiles')
        .select('id')
        .eq('is_active', true);
      
      if (!allFreeProfiles || allFreeProfiles.length === 0) {
        return 0;
      }
      
      // Filter out truly active subscribers (expired/cancelled/past-end are considered free)
      const freeProfileIds = allFreeProfiles
        .filter(p => !trulyActiveUserIds.has(p.id))
        .map(p => p.id);
      
      if (freeProfileIds.length === 0) {
        return 0;
      }
      
      // Fetch reading time for all free users and aggregate
      const { data: readingData } = await supabase
        .from('book_reads')
        .select('user_id, active_seconds')
        .in('user_id', freeProfileIds);
      
      // Aggregate total reading time per user
      const readingTimeMap = new Map<number, number>();
      readingData?.forEach((read: { user_id: number; active_seconds: number | null }) => {
        if (read.user_id && read.active_seconds) {
          const currentTotal = readingTimeMap.get(read.user_id) || 0;
          readingTimeMap.set(read.user_id, currentTotal + read.active_seconds);
        }
      });
      
      // Count users who meet the reading time requirement
      const qualifiedCount = freeProfileIds.filter(userId => {
        const totalSeconds = readingTimeMap.get(userId) || 0;
        return totalSeconds >= minSeconds;
      }).length;
      
      return qualifiedCount;
    }
    
    // Optimize for "free" subscription status only
    if (criteria.subscription_status === 'free') {
      // Get truly active subscription user IDs based on LATEST subscription
      // Users with expired, cancelled, or past-end subscriptions should be considered "free"
      const latestSubscriptions = await getLatestSubscriptions();
      const now = new Date();
      
      // Filter to only truly active subscriptions (status='active' AND subscription_end > NOW())
      // Note: subscription_end must be > NOW(), NULL values are excluded
      const trulyActiveUserIds = new Set(
        Array.from(latestSubscriptions.entries())
          .filter(([_, sub]) => {
            // Must be active status
            if (sub.status !== 'active') return false;
            // Include only if subscription_end exists AND is in the future
            return sub.subscription_end && new Date(sub.subscription_end) > now;
          })
          .map(([userId, _]) => userId)
      );
      
      // Count all active profiles
      const { count: totalCount } = await supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true })
        .eq('is_active', true);
      
      // Subtract truly active subscribers (expired/cancelled/past-end are considered free)
      // This is an approximation but much faster than fetching all users
      return Math.max(0, (totalCount || 0) - trulyActiveUserIds.size);
    }
    
    // Optimize for "active" subscription status (status='active' AND subscription_end > NOW() on LATEST subscription)
    if (criteria.subscription_status === 'active') {
      const latestSubscriptions = await getLatestSubscriptions();
      const now = new Date();
      
      // Filter to only truly active subscriptions (status='active' AND subscription_end > NOW())
      // Note: subscription_end must be > NOW(), NULL values are excluded
      const trulyActiveUserIds = new Set(
        Array.from(latestSubscriptions.entries())
          .filter(([_, sub]) => {
            // Must be active status
            if (sub.status !== 'active') return false;
            // Include only if subscription_end exists AND is in the future
            return sub.subscription_end && new Date(sub.subscription_end) > now;
          })
          .map(([userId, _]) => userId)
      );
      
      return trulyActiveUserIds.size;
    }
    
    // Optimize for "expired" subscription status (subscription_end < NOW() on LATEST subscription)
    if (criteria.subscription_status === 'expired') {
      const latestSubscriptions = await getLatestSubscriptions();
      const now = new Date();
      
      // Count users whose latest subscription is expired
      const expiredCount = Array.from(latestSubscriptions.values())
        .filter(sub => sub.subscription_end && new Date(sub.subscription_end) < now)
        .length;
      
      return expiredCount;
    }
    
    // Optimize for "cancelled" subscription status
    if (criteria.subscription_status === 'cancelled') {
      const { count } = await supabase
        .from('user_subscriptions')
        .select('user_id', { count: 'exact', head: true })
        .eq('status', 'cancelled');
      return count || 0;
    }
    
    // Optimize for "no_plan" subscription status (users who have never had any subscription)
    if (criteria.subscription_status === 'no_plan') {
      // Get all active profiles
      const { count: totalCount } = await supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true })
        .eq('is_active', true);
      
      // Get all users who have ever had a subscription
      const { data: allSubscriptions } = await supabase
        .from('user_subscriptions')
        .select('user_id');
      
      const subscribedUserIds = new Set(allSubscriptions?.map(row => row.user_id) || []);
      
      // Count = total active profiles - users with subscriptions
      return Math.max(0, (totalCount || 0) - subscribedUserIds.size);
    }
    
    // For complex criteria, we still need to use the existing approach
    // This could be further optimized with database functions
    const users = await getUsersByCriteria(criteria);
    return users.length;
  } catch (error) {
    console.error("Error counting users by criteria:", error);
    throw error;
  }
};

// Function to get users for predefined groups
export const getUsersForPredefinedGroup = async (groupName: string): Promise<GroupMember[]> => {
  const predefinedCriteria: Record<string, GroupCriteria> = {
    "Active Book Readers": { books_read_count: 3 },
    "Audio Content Consumers": { audio_plays_count: 3 },
    "Multi-Format Consumers": { books_read_count: 2, audio_plays_count: 2 },
    "Content Completion Champions": { completion_rate: 80 },
    "Engaged Highlighters": { highlights_count: 3 },
    "Feedback Providers": { feedback_count: 1 },
    "Category Enthusiasts": { selected_categories_count: 3 },
    "Content Discovery Users": { book_views_count: 10 },
    "Subscription Holders": { subscription_status: "active" },
    
    // Free User Groups
    "Free Content Users": { subscription_status: "free" },
    "Free Content Users with 30+ Minutes Reading": { 
      subscription_status: "free", 
      total_reading_minutes: 30 
    },
    "Free Users - No Plan Ever": { subscription_status: "no_plan" },
    "Expired Subscribers": { subscription_status: "expired" },
    "Cancelled Subscribers": { subscription_status: "cancelled" },
    "Free Users with Account Age 30+ Days": { 
      subscription_status: "free", 
      account_age_min_days: 30 
    },
    "Free Users with Account Age 90+ Days": { 
      subscription_status: "free", 
      account_age_min_days: 90 
    },
    "Free Users with 5+ Books Read": { 
      subscription_status: "free", 
      books_read_count: 5 
    },
    "Free Users with 60+ Minutes Reading": { 
      subscription_status: "free", 
      total_reading_minutes: 60 
    },
    "Free Users with 10+ Books Read": { 
      subscription_status: "free", 
      books_read_count: 10 
    },
    "Free Users with 2+ Hours Reading": { 
      subscription_status: "free", 
      total_reading_minutes: 120 
    },
    "Expired Subscribers with 30+ Days Account Age": { 
      subscription_status: "expired", 
      account_age_min_days: 30 
    },
    "Cancelled Subscribers with Reading Activity": { 
      subscription_status: "cancelled", 
      books_read_count: 1 
    },
    "Free Users - High Engagement": { 
      subscription_status: "free", 
      books_read_count: 5,
      total_reading_minutes: 60,
      account_age_min_days: 30
    },
    
    // Other Groups
    "High-Value Subscribers": { subscription_duration_days: 30 },
    "At-Risk Churn Users": { last_activity_days: 14 },
    "Recent Registrations": { registration_days: 30 },
    "Inactive Users": { last_activity_days: 60 },
    "Engagement Leaders": { overall_engagement_score: 90 },
    "Age Group Specific": { age_group: "specific" },
    "Referral Users": { referred_by: "not_null" },
    "Platform Specific Users": { platform_preference: "specific" },
    "Feature Adopters": { highlights_used: true, feedback_provided: true },
    "High Completion Rate Users": { completion_rate: 90 }
  };

  const criteria = predefinedCriteria[groupName];
  if (!criteria) {
    throw new Error(`Unknown predefined group: ${groupName}`);
  }

  return getUsersByCriteria(criteria);
};

// Function to count users for predefined groups (optimized with database-level COUNT)
export const countUsersForPredefinedGroup = async (groupName: string): Promise<number> => {
  try {
    // For all predefined groups, use the criteria-based counting
    const predefinedCriteria: Record<string, GroupCriteria> = {
      "Active Book Readers": { books_read_count: 3 },
      "Audio Content Consumers": { audio_plays_count: 3 },
      "Multi-Format Consumers": { books_read_count: 2, audio_plays_count: 2 },
      "Content Completion Champions": { completion_rate: 80 },
      "Engaged Highlighters": { highlights_count: 3 },
      "Feedback Providers": { feedback_count: 1 },
      "Category Enthusiasts": { selected_categories_count: 3 },
      "Content Discovery Users": { book_views_count: 10 },
      "Subscription Holders": { subscription_status: "active" },
      "Free Content Users": { subscription_status: "free" },
      "Free Content Users with 30+ Minutes Reading": { 
        subscription_status: "free", 
        total_reading_minutes: 30 
      },
      "Free Users - No Plan Ever": { subscription_status: "no_plan" },
      "Expired Subscribers": { subscription_status: "expired" },
      "Cancelled Subscribers": { subscription_status: "cancelled" },
      "Free Users with Account Age 30+ Days": { 
        subscription_status: "free", 
        account_age_min_days: 30 
      },
      "Free Users with Account Age 90+ Days": { 
        subscription_status: "free", 
        account_age_min_days: 90 
      },
      "Free Users with 5+ Books Read": { 
        subscription_status: "free", 
        books_read_count: 5 
      },
      "Free Users with 60+ Minutes Reading": { 
        subscription_status: "free", 
        total_reading_minutes: 60 
      },
      "Free Users with 10+ Books Read": { 
        subscription_status: "free", 
        books_read_count: 10 
      },
      "Free Users with 2+ Hours Reading": { 
        subscription_status: "free", 
        total_reading_minutes: 120 
      },
      "Expired Subscribers with 30+ Days Account Age": { 
        subscription_status: "expired", 
        account_age_min_days: 30 
      },
      "Cancelled Subscribers with Reading Activity": {
        subscription_status: "cancelled", 
        books_read_count: 1 
      },
      "Free Users - High Engagement": { 
        subscription_status: "free", 
        books_read_count: 5,
        total_reading_minutes: 60,
        account_age_min_days: 30
      },
      "High-Value Subscribers": { subscription_duration_days: 30 },
      "At-Risk Churn Users": { last_activity_days: 14 },
      "Recent Registrations": { registration_days: 30 },
      "Inactive Users": { last_activity_days: 60 },
      "Engagement Leaders": { overall_engagement_score: 90 },
      "Age Group Specific": { age_group: "specific" },
      "Referral Users": { referred_by: "not_null" },
      "Platform Specific Users": { platform_preference: "specific" },
      "Feature Adopters": { highlights_used: true, feedback_provided: true },
      "High Completion Rate Users": { completion_rate: 90 }
    };

    const criteria = predefinedCriteria[groupName];
    if (!criteria) {
      throw new Error(`Unknown predefined group: ${groupName}`);
    }

    return await countUsersByCriteria(criteria);
  } catch (error) {
    console.error("Error counting users for predefined group:", error);
    throw error;
  }
};

// Function to get all unique users across all groups
export const getTotalUniqueMembers = async (): Promise<number> => {
  try {
    // Get all user groups
    const { data: groups, error: groupsError } = await supabase
      .from('user_groups')
      .select('id, name, criteria')
      .eq('is_active', true);

    if (groupsError) {
      console.error("Error fetching user groups:", groupsError);
      throw groupsError;
    }

    if (!groups || groups.length === 0) {
      return 0;
    }

    // Collect all unique user IDs from all groups
    const allUserIds = new Set<number>();

    // Process each group to get its members
    for (const group of groups) {
      try {
        let members: GroupMember[] = [];

        // Check if it's a predefined group
        const predefinedGroups = [
          "Active Book Readers", "Audio Content Consumers", "Multi-Format Consumers",
          "Content Completion Champions", "Engaged Highlighters", "Feedback Providers",
          "Category Enthusiasts", "Content Discovery Users", "Subscription Holders",
          "Free Content Users", "Free Content Users with 30+ Minutes Reading",
          "Free Users - No Plan Ever", "Expired Subscribers", "Cancelled Subscribers",
          "Free Users with Account Age 30+ Days", "Free Users with Account Age 90+ Days",
          "Free Users with 5+ Books Read", "Free Users with 60+ Minutes Reading",
          "Free Users with 10+ Books Read", "Free Users with 2+ Hours Reading",
          "Expired Subscribers with 30+ Days Account Age",
          "Cancelled Subscribers with Reading Activity", "Free Users - High Engagement",
          "High-Value Subscribers", "At-Risk Churn Users", "Recent Registrations",
          "Inactive Users", "Engagement Leaders", "Age Group Specific",
          "Referral Users", "Platform Specific Users", "Feature Adopters",
          "High Completion Rate Users"
        ];

        if (predefinedGroups.includes(group.name)) {
          members = await getUsersForPredefinedGroup(group.name);
        } else {
          // Custom group with criteria
          members = await getUsersByCriteria(group.criteria as GroupCriteria);
        }

        // Add user IDs to the set
        members.forEach(member => {
          allUserIds.add(member.user_id);
        });
      } catch (groupError) {
        console.error(`Error processing group ${group.name}:`, groupError);
        // Continue with other groups even if one fails
      }
    }

    return allUserIds.size;
  } catch (error) {
    console.error("Error in getTotalUniqueMembers:", error);
    throw error;
  }
};

// Function to refresh all group member counts
export const refreshGroupMemberCount = async (groupId: number, groupName: string, criteria?: GroupCriteria): Promise<{
  success: boolean;
  memberCount: number;
  error?: string;
}> => {
  try {
    let memberCount = 0;
    
    // Check if it's a predefined group
    const predefinedGroups = [
      "Active Book Readers", "Audio Content Consumers", "Multi-Format Consumers",
      "Content Completion Champions", "Engaged Highlighters", "Feedback Providers",
      "Category Enthusiasts", "Content Discovery Users", "Subscription Holders",
      "Free Content Users", "Free Content Users with 30+ Minutes Reading",
      "High-Value Subscribers", "At-Risk Churn Users", "Recent Registrations",
      "Inactive Users", "Engagement Leaders", "Age Group Specific",
      "Referral Users", "Platform Specific Users", "Feature Adopters",
      "High Completion Rate Users", "Free Users - No Plan Ever", "Expired Subscribers",
      "Cancelled Subscribers", "Free Users with Account Age 30+ Days",
      "Free Users with Account Age 90+ Days", "Free Users with 5+ Books Read",
      "Free Users with 60+ Minutes Reading", "Free Users with 10+ Books Read",
      "Free Users with 2+ Hours Reading", "Expired Subscribers with 30+ Days Account Age",
      "Cancelled Subscribers with Reading Activity", "Free Users - High Engagement"
    ];

    if (predefinedGroups.includes(groupName)) {
      memberCount = await countUsersForPredefinedGroup(groupName);
    } else if (criteria) {
      // Custom group with criteria
      memberCount = await countUsersByCriteria(criteria);
    }

    // Update the group with new count
    const { error: updateError } = await supabase
      .from('user_groups')
      .update({
        member_count: memberCount,
        last_refreshed_at: new Date().toISOString()
      })
      .eq('id', groupId);

    if (updateError) {
      return {
        success: false,
        memberCount: 0,
        error: updateError.message
      };
    }

    return {
      success: true,
      memberCount
    };
  } catch (error) {
    console.error("Error in refreshGroupMemberCount:", error);
    return {
      success: false,
      memberCount: 0,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

export const refreshAllGroupMemberCounts = async (): Promise<{
  success: boolean;
  updated: number;
  errors: string[];
}> => {
  try {
    const { data: groups, error: groupsError } = await supabase
      .from('user_groups')
      .select('id, name, criteria');

    if (groupsError) {
      throw groupsError;
    }

    let updated = 0;
    const errors: string[] = [];

    for (const group of groups || []) {
      try {
        let memberCount = 0;
        
        // Check if it's a predefined group
        const predefinedGroups = [
          "Active Book Readers", "Audio Content Consumers", "Multi-Format Consumers",
          "Content Completion Champions", "Engaged Highlighters", "Feedback Providers",
          "Category Enthusiasts", "Content Discovery Users", "Subscription Holders",
          "Free Content Users", "Free Content Users with 30+ Minutes Reading",
          "High-Value Subscribers", "At-Risk Churn Users", "Recent Registrations",
          "Inactive Users", "Engagement Leaders", "Age Group Specific",
          "Referral Users", "Platform Specific Users", "Feature Adopters",
          "High Completion Rate Users"
        ];

        if (predefinedGroups.includes(group.name)) {
          memberCount = await countUsersForPredefinedGroup(group.name);
        } else {
          // Custom group with criteria
          memberCount = await countUsersByCriteria(group.criteria as GroupCriteria);
        }

        // Update the group with new count
        const { error: updateError } = await supabase
          .from('user_groups')
          .update({
            member_count: memberCount,
            last_refreshed_at: new Date().toISOString()
          })
          .eq('id', group.id);

        if (updateError) {
          errors.push(`Failed to update group ${group.name}: ${updateError.message}`);
        } else {
          updated++;
        }
      } catch (error) {
        errors.push(`Error processing group ${group.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    return {
      success: errors.length === 0,
      updated,
      errors
    };
  } catch (error) {
    console.error("Error in refreshAllGroupMemberCounts:", error);
    throw error;
  }
};
